/**
 * Inventory-backed search suggestions for the type-ahead dropdown (RAS-2536).
 *
 * Suggestions come from the `search_terms` facet on the listings index, written by
 * server/api-util/searchTerms.js. Because a term can only exist there if a real published
 * listing produced it, "never suggest what we don't have" is structural rather than a rule we
 * enforce here. The facet count is the number of matching listings, which Algolia already
 * sorts by — so the most-stocked phrase leads without any ranking work on our side.
 *
 * Queried straight from the browser with the SEARCH-ONLY key: Algolia's edge network is faster
 * than a round-trip through our dyno, and this path never touches the write-capable key.
 *
 * Deliberately free of React, debouncing and caching — those belong to the hook that calls
 * this. Keeping it a plain async function is what makes it testable without a DOM.
 */

import algoliasearch from 'algoliasearch/lite';
import { getAlgoliaIndexNameForLang } from './urlHelpers';
import { isSuggestionLocale } from './suggestionLocales';

// Below two characters the facet matches almost everything, which is noise rather than help.
const MIN_QUERY_LENGTH = 2;

const DEFAULT_LIMIT = 6;

/**
 * Radius for the first pass, in metres.
 *
 * Wider than the results page's own expansion steps (EXPANSION_STEPS_KM = [5,10,20,40,80] in
 * server/api-util/algoliaSearchHelpers.js) on the theory that suggestions should broaden
 * faster than results: an empty dropdown is worse than a slightly distant suggestion.
 */
const NEARBY_RADIUS_METRES = 150000;

const PUBLISHED_FILTER = 'state:published';

const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
const searchKey = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;

// Null when unconfigured, which makes the whole feature inert instead of throwing — the same
// guard the rest of the app uses (see AlgoliaSearchPage.duck.js).
const searchClient = appId && searchKey ? algoliasearch(appId, searchKey) : null;

/**
 * Cross-instance request sharing.
 *
 * The search page mounts BOTH search inputs at once — the mobile one is hidden with CSS rather
 * than unmounted (see the comment above the mobile block in AlgoliaSearchSection). Each mounts
 * its own hook, so a single keystroke produced two identical two-tier lookups: four requests
 * where one pair was needed, confirmed in a network capture.
 *
 * The hook's own cache cannot help, because it is per instance. These two module-level maps
 * are: concurrent callers share one promise, and a resolved result is reused briefly
 * afterwards for the case where the second instance asks a moment later rather than
 * simultaneously.
 */
const inFlight = new Map();
const recent = new Map();
const RECENT_TTL_MS = 30000;
const RECENT_LIMIT = 50;

const readRecent = key => {
  const hit = recent.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > RECENT_TTL_MS) {
    recent.delete(key);
    return null;
  }
  return hit.results;
};

const writeRecent = (key, results) => {
  recent.set(key, { results, at: Date.now() });
  if (recent.size > RECENT_LIMIT) recent.delete(recent.keys().next().value);
};

/** Exposed so tests can start from a clean slate; not used by application code. */
export const __clearSuggestionCache = () => {
  inFlight.clear();
  recent.clear();
};

const isFacetNotSearchableError = error =>
  /searchable\(/i.test(error?.message || '') || /not searchable/i.test(error?.message || '');

const runFacetQuery = async (index, query, { filters, aroundLatLng, aroundRadius, limit }) => {
  const response = await index.searchForFacetValues('search_terms', query, {
    filters,
    maxFacetHits: limit,
    ...(aroundLatLng ? { aroundLatLng } : {}),
    ...(aroundLatLng && aroundRadius ? { aroundRadius } : {}),
  });

  return (response?.facetHits || []).map(hit => ({ value: hit.value, count: hit.count }));
};

/**
 * Fetch suggestions for a partially typed query.
 *
 * @param {Object} params
 * @param {string} params.query - what the customer has typed so far
 * @param {string} [params.locale='en'] - UI locale; gated by SUGGESTION_LOCALES
 * @param {string} [params.aroundLatLng] - "lat,lng" of the customer's selected search location
 * @param {string} [params.filters] - overrides the default published filter. The store page
 *   passes its own baseFilters, which already carry user.id and category visibility
 * @param {boolean} [params.useLocation=true] - false for store search, where a radius is
 *   meaningless because one store is one location
 * @param {number} [params.limit=6]
 * @returns {Promise<Array<{value: string, count: number}>>} never rejects; [] on any failure
 */
const runFetch = async ({
  query,
  locale = 'en',
  aroundLatLng = null,
  filters = PUBLISHED_FILTER,
  useLocation = true,
  limit = DEFAULT_LIMIT,
} = {}) => {
  const trimmed = String(query == null ? '' : query).trim();

  // Cheap exits first — none of these should cost a network call.
  if (!searchClient) return [];
  if (trimmed.length < MIN_QUERY_LENGTH) return [];
  if (!isSuggestionLocale(locale)) return [];

  const index = searchClient.initIndex(getAlgoliaIndexNameForLang(locale));
  const geo = useLocation ? aroundLatLng : null;

  try {
    // Every result is awaited before returning. Returning the promise directly would let a
    // rejection escape the catch below, since a `return promise` inside try/catch is not
    // covered by it — which silently broke the no-location path until a test caught it.
    if (!geo) {
      const everywhere = await runFacetQuery(index, trimmed, { filters, limit });
      return everywhere;
    }

    // Pass 1 — restricted to the customer's area. A real radius is required: aroundRadius:'all'
    // ranks by distance but does NOT filter, so facet counts would come back identical to no
    // geo at all and "nearby" would silently mean nothing.
    const nearby = await runFacetQuery(index, trimmed, {
      filters,
      aroundLatLng: geo,
      aroundRadius: NEARBY_RADIUS_METRES,
      limit,
    });

    if (nearby.length >= limit) return nearby;

    // Pass 2 — broaden. Verified necessary against real data: "camera" within 150km of Ottawa
    // returned nothing while the unrestricted query returned six suggestions. Without this the
    // dropdown would be empty for most searches with a location set.
    const broader = await runFacetQuery(index, trimmed, { filters, limit });

    const seen = new Set(nearby.map(s => s.value));
    return [...nearby, ...broader.filter(s => !seen.has(s.value))].slice(0, limit);
  } catch (error) {
    if (isFacetNotSearchableError(error)) {
      // The index is missing `searchable(search_terms)`. Loud, because it is a configuration
      // drift that would otherwise present as "suggestions just never appear".
      console.error(
        '[suggestions] search_terms is not a searchable facet on this index. ' +
          'Run server/scripts/configure-suggestion-facet.js for this environment.',
        error.message
      );
    }
    // Never let a suggestion failure interfere with typing or with search itself.
    return [];
  }
};

export const __testing = {
  MIN_QUERY_LENGTH,
  NEARBY_RADIUS_METRES,
  DEFAULT_LIMIT,
  PUBLISHED_FILTER,
};

/**
 * Public entry point: shares a single request between everything asking the same question at
 * roughly the same moment. See the note on `inFlight` above for why that is necessary here.
 */
export const fetchSuggestions = async (params = {}) => {
  const {
    query,
    locale = 'en',
    aroundLatLng = null,
    filters = PUBLISHED_FILTER,
    useLocation = true,
    limit = DEFAULT_LIMIT,
  } = params;

  const trimmed = String(query == null ? '' : query).trim();
  const key = [locale, useLocation ? aroundLatLng || '' : '', filters, limit, trimmed].join('|');

  const cached = readRecent(key);
  if (cached) return cached;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = runFetch(params)
    .then(results => {
      writeRecent(key, results);
      return results;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
};
